import React from 'react';
import { Box, Typography, Link, Tooltip, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { formatEventTimestamp } from '../../../utils/dateUtils';
import StatusChip from '../../../components/StatusChip';
import { getEventStatusColor } from '../../../utils/statusChipUtil';
import { useResendHandler } from '../dataHandlers/ResendHandler';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';
import { WRITE_WEBHOOK_EVENTS } from '../../../constants/webhookPermissions';

/**
 * EventSummaryHeader component displays event metadata in the detail pane
 * @param {Object} eventDetails - Event details object
 * @param eventId
 */
const EventSummaryHeader = ({ eventDetails }) => {
  const eventId = eventDetails.event.id;
  const { resendEvent, isLoading } = useResendHandler({ eventId });
  const { hasWebhookPermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const canResend = hasWebhookPermission(WRITE_WEBHOOK_EVENTS);

  // Helper to render a field
  const renderField = (label, value, isLink = false, isMonospace = false) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const displayValue = isLink ? (
      <Link
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontSize: '0.875rem',
          textDecoration: 'underline',
          color: 'primary.main',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {value}
      </Link>
    ) : (
      <Typography
        variant="body2"
        color="text.primary"
        sx={{
          fontFamily: isMonospace ? 'monospace' : 'inherit',
          fontSize: '0.875rem',
        }}
      >
        {value}
      </Typography>
    );

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.75 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '0.875rem', fontWeight: 400, minWidth: '140px', flexShrink: 0 }}
        >
          {label}
        </Typography>
        <Box sx={{ flex: 1, justifyContent: 'flex-start', ml: 2 }}>{displayValue}</Box>
      </Box>
    );
  };

  // Extract event object (API returns { event: {...}, request_payload: {...} })
  const event = eventDetails.event || eventDetails;

  // Get field values from event object
  const originDate = event.created_at; // Origin date is when the event was created
  const resourceId = event.resource_id;
  const resourceUrl = event.resource_url;
  const eventStatus = event.status;
  const profileId = event.profile_id;
  const name = event.name;

  // Check if next_attempt_at is a valid date (not a zero/default date)
  const nextAttemptAtRaw = event.next_attempt_at;
  const nextAttemptAt = nextAttemptAtRaw && !nextAttemptAtRaw.startsWith('0001-01-01') ? nextAttemptAtRaw : null;

  const handleResendButton = (e) => {
    e.preventDefault();
    if (!canResend) {
      return;
    }
    resendEvent();
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Event Type as Heading with Resend Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: '1.125rem',
            fontWeight: 600,
            fontFamily: 'monospace',
            color: 'text.primary',
            lineHeight: 1.2,
          }}
        >
          {event.event_type || 'Event'}
        </Typography>
        <PermissionDeniedWrapper show={!canResend} title={accessDeniedMessage}>
          <Tooltip title={canResend ? 'Resend this event to your webhook URL immediately.' : ''}>
            <span>
              <Button
                size="small"
                disabled={!canResend || isLoading}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
                onClick={handleResendButton}
              >
                <RefreshIcon fontSize="small" />
                {isLoading ? 'Delivering...' : 'Resend Event'}
              </Button>
            </span>
          </Tooltip>
        </PermissionDeniedWrapper>
      </Box>
      {/* Event Details Fields */}
      <Box>
        {renderField('Event ID', eventId, false, true)}
        {renderField('Origin date', originDate ? formatEventTimestamp(originDate) : null)}
        {renderField('Resource ID', resourceId, false, true)}
        {renderField('Resource URL', resourceUrl, true)}
        {renderField('Profile ID', profileId, false, true)}
        {renderField('Destination name', name, false, true)}
        {eventStatus && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.875rem', fontWeight: 400, minWidth: '140px', flexShrink: 0 }}
            >
              Event Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-start', ml: 2 }}>
              <StatusChip color={getEventStatusColor(eventStatus)} label={eventStatus} size="small" />
            </Box>
          </Box>
        )}
        {renderField('Next attempt at', nextAttemptAt ? formatEventTimestamp(nextAttemptAt) : null)}
      </Box>
    </Box>
  );
};

export default EventSummaryHeader;
